import {
  Button,
  FieldSelect,
  FieldText,
  FieldWrapper,
  FlexBox,
  FormField,
  FormGrid,
  Page,
  Spinner,
  Text,
} from '@dixa/lib-shared-habla';
import { Satisfaction } from 'lib-shared-legacy-types';
import { useMount } from 'react-use';
import {
  Field,
  Form,
  FormSection,
  InjectedFormProps,
  reduxForm,
} from 'redux-form';
import { required } from 'redux-form-validators';

import {
  ChatCustomizationDispatchProps,
  ChatCustomizationStateProps,
} from 'components/settings/containers/sections/satisfaction/chatCustomization';
import { RouteComponentProps } from 'hocs/router';

import supportedLanguages from '../../../../../../../app/utils/supportedLanguages';

import { CSATFollowUpPreview } from './CSATFollowUpPreview';
import { CSATRatingPreview } from './CSATRatingPreview';
import { CSATThankYouPreview } from './CSATThankYouPreview';

export type ChatCustomizationOwnPropsWithLanguage =
  RouteComponentProps<'language'>;

export interface InitialValues {
  customization: Satisfaction.ChatRatingCustomization | null;
}

type ChatCustomizationProps = ChatCustomizationStateProps &
  ChatCustomizationDispatchProps & {
    onSubmit: (fields: Partial<Satisfaction.ChatCsatSettings>) => void;
  };

type InjectedProps = InjectedFormProps<{}, ChatCustomizationProps>;

const ChatCustomization = (
  props: ChatCustomizationProps & InjectedProps,
): JSX.Element | null => {
  useMount(() => {
    const { loadGlobalSettings, loadLanguageSettings } = props;
    loadGlobalSettings();
    loadLanguageSettings();
  });

  const handleSave = () => {
    const { isSetup, change } = props;
    change('enabled', isSetup ? false : null);
  };

  const handleSaveAndEnable = () => {
    const { change } = props;
    change('enabled', true);
  };

  const {
    asyncStatus,
    ratingQuestionPreview,
    ratingStylePreview,
    followUpQuestionPreview,
    placeholderTextPreview,
    thankYouMessagePreview,
    language: languageCode,
    isSetup,
    invalid,
    submitting,
  } = props;

  const language = supportedLanguages.find(({ code }) => code === languageCode);

  if (asyncStatus === 'loading') {
    <FlexBox height="100%" alignItems="center" justifyContent="center">
      <Spinner />
    </FlexBox>;
  }

  return (
    <Page.Section
      title={language?.label}
      controls={
        <>
          <Button
            variant={isSetup ? 'secondary' : 'primary'}
            disabled={invalid || submitting}
            onClick={handleSave}
          >
            Save
          </Button>

          {isSetup && (
            <Button
              disabled={invalid || submitting}
              onClick={handleSaveAndEnable}
            >
              Save and enable
            </Button>
          )}
        </>
      }
    >
      <Form>
        <FormSection name="customization">
          <FormGrid>
            <FormField span={3}>
              <FieldWrapper label="Survey look">
                <Text.Paragraph>
                  Choose the appearance of your CSAT rating request here. You
                  can customize and translate all questions and texts as well as
                  choose a rating style for each language. You can also switch
                  the preview to see how your selections will look on each
                  channel.
                </Text.Paragraph>
              </FieldWrapper>
            </FormField>

            <FormField span={6}>
              <FieldWrapper label="Rating question">
                <FlexBox gap="400">
                  <CSATRatingPreview
                    question={ratingQuestionPreview}
                    ratingStyle={ratingStylePreview}
                  />

                  <FlexBox flexDirection="column" flex="1">
                    <Field
                      validate={[required()]}
                      name="rating_question"
                      label="Rating question"
                      component={FieldText}
                    />
                    <Field
                      validate={[required()]}
                      name="look_and_feel"
                      label="Rating style"
                      items={[
                        { id: 'emojis', name: 'Emojis' },
                        { id: 'stars', name: 'Stars' },
                        { id: 'numbers', name: 'Numbers' },
                      ]}
                      component={FieldSelect}
                    />
                  </FlexBox>
                </FlexBox>
              </FieldWrapper>
            </FormField>

            <FormField span={6}>
              <FieldWrapper label="Follow-up question">
                <FlexBox gap="400">
                  <CSATFollowUpPreview
                    question={followUpQuestionPreview}
                    placeholder={placeholderTextPreview}
                  />

                  <FlexBox flexDirection="column" flex="1">
                    <Field
                      validate={[required()]}
                      name="rating_follow_up_question"
                      label="Follow-up question"
                      component={FieldText}
                    />
                    <Field
                      validate={[required()]}
                      name="rating_placeholder_text"
                      label="Placeholder text"
                      component={FieldText}
                    />
                  </FlexBox>
                </FlexBox>
              </FieldWrapper>
            </FormField>

            <FormField span={6}>
              <FieldWrapper label="Thank you message">
                <FlexBox gap="400">
                  <CSATThankYouPreview message={thankYouMessagePreview} />

                  <FlexBox flexDirection="column" flex="1">
                    <Field
                      validate={[required()]}
                      name="rating_completed_message"
                      label="Thank you message"
                      component={FieldText}
                    />
                  </FlexBox>
                </FlexBox>
              </FieldWrapper>
            </FormField>
          </FormGrid>
        </FormSection>
      </Form>
    </Page.Section>
  );
};

const FORM_NAME = 'satisfactionChatCustomization';

export default reduxForm<{}, ChatCustomizationProps>({
  form: FORM_NAME,
  enableReinitialize: true,
})(ChatCustomization);
